import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, Printer } from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router'
import type { PickOutcome, PullSheet, PullSheetLine, VisitOutcome } from '@fitting-room/contracts'
import { usePullSheet, useRecordOutcomes } from '../../lib/adminQueries'
import { boutiqueDateTime } from '../../lib/boutique'
import { useStaffToken } from '../../lib/staffToken'

/**
 * Outcome vocabularies come from the contract's own enums rather than being
 * retyped, so a new outcome is a contract change and a compile error here.
 */
const PICK_OUTCOMES: readonly PickOutcome[] = ['not_pulled', 'pulled', 'tried', 'loved', 'ruled_out']
const VISIT_OUTCOMES: readonly VisitOutcome[] = ['pending', 'said_yes', 'returning', 'nothing_worked']

const humanize = (key: string) => key.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/**
 * The sizes hanging in the shop, where there are any.
 *
 * This used to say "No sample on this floor — order only" whenever a line
 * carried no pieces, which now means EVERY made-to-order line on every sheet:
 * the shop keeps no sample register, made to order IS the list they can try, and
 * the customer-facing version of this sentence was removed for exactly that
 * reason. Leaving it here left the same untrue claim on the one page a stylist
 * actually works from, printed against every line of it.
 *
 * So absence is silent. An off-the-rack line has real pieces and prints their
 * sizes and barcodes; a made-to-order line has nothing to say about stock, and
 * says it.
 */
function SampleCell({ line }: { readonly line: PullSheetLine }) {
  if (line.noSampleAtLocation || line.samples.length === 0) {
    return <span className="text-[color:var(--st-ink-faint)]">—</span>
  }
  return (
    <ul className="space-y-0.5">
      {line.samples.map((sample, index) => (
        <li key={`${sample.size}-${sample.rackCode ?? ''}-${index}`} className="whitespace-nowrap">
          <span className="font-semibold tabular-nums">{sample.size}</span>
          {sample.rackCode ? <span className="ml-2 text-[color:var(--st-ink-dim)]">rack {sample.rackCode}</span> : null}
          {sample.status !== 'available' ? <span className="ml-2 text-[color:#6d5312]">{humanize(sample.status)}</span> : null}
          {sample.heldForVisitId ? <span className="ml-2 text-[color:#96432f]">held for another visit</span> : null}
        </li>
      ))}
    </ul>
  )
}

function LineRow({
  line,
  outcome,
  onOutcome,
  readOnly,
}: {
  readonly line: PullSheetLine
  readonly outcome: PickOutcome
  readonly onOutcome: (value: PickOutcome) => void
  /** An earlier version of the list: a record of what was sent, not a form. */
  readonly readOnly: boolean
}) {
  /*
   * The sheet is printed and carried onto the floor, and a broken-image glyph
   * survives that trip as a black box on paper. Upstream's attachment URLs
   * expire (see `GarmentCardFace`), so "the photograph did not arrive" is a
   * state this page reaches routinely, and it already has a frame for a line
   * with no photograph at all.
   */
  const [broken, setBroken] = useState(false)
  const imageUrl = broken ? undefined : line.imageUrl
  return (
    <tr className="print-line align-top">
      <td className="w-10 text-center tabular-nums text-[color:var(--st-ink-dim)]">{line.position + 1}</td>
      <td className="w-20">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-24 w-16 border object-cover"
            style={{ borderColor: 'var(--st-rule)' }}
            onError={() => setBroken(true)}
          />
        ) : (
          <div className="flex h-24 w-16 items-center justify-center border text-xs text-[color:var(--st-ink-faint)]" style={{ borderColor: 'var(--st-rule)' }}>
            no photo
          </div>
        )}
      </td>
      <td>
        <div className="font-semibold">{line.name}</div>
        {line.code ? <div className="text-xs tabular-nums text-[color:var(--st-ink-dim)]">{line.code}</div> : null}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[color:var(--st-ink-dim)]">
          {Object.entries(line.attributes).map(([key, value]) => (
            <span key={key}>
              <span className="text-[color:var(--st-ink-faint)]">{humanize(key)}</span> {value}
            </span>
          ))}
        </div>
        {line.addedBy === 'staff' ? <div className="mt-1 text-xs text-[color:var(--st-gold-deep)]">added by staff</div> : null}
        {line.round > 0 ? <div className="text-xs text-[color:var(--st-ink-faint)]">round {line.round}</div> : null}
      </td>
      <td className="text-xs">
        <SampleCell line={line} />
      </td>
      <td className="max-w-[16rem] text-sm">
        {line.note ? <span className="italic">“{line.note}”</span> : <span className="text-[color:var(--st-ink-faint)]">—</span>}
      </td>
      <td>
        {readOnly ? (
          <span className="text-xs text-[color:var(--st-ink-dim)]">{humanize(outcome)}</span>
        ) : (
          <fieldset className="flex flex-wrap gap-x-3 gap-y-1">
            <legend className="sr-only">Outcome for {line.name}</legend>
            {PICK_OUTCOMES.map((value) => (
              <label key={value} className="flex items-center gap-1 whitespace-nowrap text-xs">
                <input
                  type="radio"
                  name={`outcome-${line.garmentId}`}
                  value={value}
                  checked={outcome === value}
                  onChange={() => onOutcome(value)}
                />
                {humanize(value)}
              </label>
            ))}
          </fieldset>
        )}
      </td>
    </tr>
  )
}

function LineTable({ lines, outcomes, onOutcome, readOnly }: {
  readonly lines: readonly PullSheetLine[]
  readonly outcomes: Readonly<Record<string, PickOutcome>>
  readonly onOutcome: (garmentId: string, value: PickOutcome) => void
  readonly readOnly: boolean
}) {
  return (
    <table className="st-table">
      <thead>
        <tr>
          <th className="w-10">#</th>
          <th className="w-20">Photo</th>
          <th>Style</th>
          <th>Sample / rack</th>
          <th>Customer note</th>
          <th>Outcome</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <LineRow
            key={line.garmentId}
            line={line}
            outcome={outcomes[line.garmentId] ?? line.outcome}
            onOutcome={(value) => onOutcome(line.garmentId, value)}
            readOnly={readOnly}
          />
        ))}
      </tbody>
    </table>
  )
}

export function PullSheetPage() {
  const { token } = useStaffToken()
  const params = useParams<{ visitId: string }>()
  const visitId = params.visitId ?? ''
  /*
   * Which version is on screen, in the URL so it can be linked and reloaded.
   * Absent means the one the boutique acts on, which is what a stylist opening
   * this page from the day view always wants.
   */
  const [search, setSearch] = useSearchParams()
  const viewing = search.get('session') ?? undefined
  const query = usePullSheet(token, visitId, viewing)
  const mutation = useRecordOutcomes(token, visitId)

  const sheet = query.data?.pullSheet
  /*
   * An earlier version is a record, not a worksheet: the outcomes belong to
   * the appointment and are recorded against the list it is actually working
   * from, so this page reads rather than writes while one is open.
   */
  const readOnly = Boolean(sheet && sheet.versions.length > 1 && !sheet.versions.some((v) => v.sessionId === sheet.sessionId && v.authoritative))
  const [outcomes, setOutcomes] = useState<Record<string, PickOutcome>>({})
  const [visitOutcome, setVisitOutcome] = useState<VisitOutcome>('pending')

  useEffect(() => {
    if (sheet) setVisitOutcome(sheet.outcome)
  }, [sheet])

  const allLines = useMemo(
    () => (sheet ? [...sheet.slots.flatMap((s) => s.lines), ...sheet.candidates] : []),
    [sheet],
  )
  const totalPicked = useMemo(() => (sheet ? sheet.slots.reduce((n, s) => n + s.lines.length, 0) : 0), [sheet])
  const neverPicked = Boolean(sheet) && totalPicked === 0

  /** One block per intake screen, in the order the questionnaire asked. */
  const profileGroups = useMemo(() => {
    const groups: Array<{ stepId: string; entries: PullSheet['profile'] }> = []
    for (const entry of sheet?.profile ?? []) {
      const last = groups[groups.length - 1]
      if (last && last.stepId === entry.stepId) last.entries.push(entry)
      else groups.push({ stepId: entry.stepId, entries: [entry] })
    }
    return groups
  }, [sheet])

  if (query.isPending) return <p className="text-sm text-[color:var(--st-ink-dim)]">Loading…</p>
  if (query.isError || !sheet) {
    return (
      <div>
        <p className="st-alert">Could not load this pull sheet.</p>
        <Link className="mt-3 inline-block text-[color:var(--st-gold-deep)] underline" to="/admin">
          Back to appointments
        </Link>
      </div>
    )
  }

  const save = () => {
    const merged: Record<string, PickOutcome> = {}
    for (const line of allLines) merged[line.garmentId] = outcomes[line.garmentId] ?? line.outcome
    mutation.mutate({ visitOutcome, pickOutcomes: merged })
  }

  return (
    <div>
      <div className="print-hide mb-4 flex flex-wrap items-center gap-2">
        <Link className="st-btn-quiet" to="/admin">
          <ChevronLeft size={14} aria-hidden />
          Appointments
        </Link>
        <span className="ml-auto" />
        <button type="button" className="st-btn-ghost" onClick={() => window.print()}>
          <Printer size={14} aria-hidden />
          Print
        </button>
        {readOnly ? (
          <button type="button" className="st-btn" onClick={() => setSearch({})}>
            Back to the current list
          </button>
        ) : (
          <button type="button" className="st-btn" disabled={mutation.isPending} onClick={save}>
            {mutation.isPending ? 'Saving…' : 'Save outcomes'}
          </button>
        )}
      </div>

      {mutation.isError ? (
        <p className="print-hide mb-3 st-alert">
          Outcomes did not save. Try again.
        </p>
      ) : null}
      {mutation.isSuccess ? (
        <p className="print-hide mb-3 st-ok">Outcomes saved.</p>
      ) : null}

      <header className="mb-4 border-b pb-3" style={{ borderColor: 'var(--st-rule-strong)' }}>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <h1 className="st-display text-2xl">{sheet.customerName ?? 'Walk-in'}</h1>
          <span className="tabular-nums">{boutiqueDateTime(sheet.scheduledAt)}</span>
          <span>{sheet.tierLabel}</span>
          {sheet.locationName ? <span className="text-[color:var(--st-ink-dim)]">{sheet.locationName}</span> : null}
          {typeof sheet.guestCount === 'number' ? <span className="text-[color:var(--st-ink-dim)]">{sheet.guestCount} guests</span> : null}
          {sheet.customerPhone ? <span className="text-[color:var(--st-ink-dim)] tabular-nums">{sheet.customerPhone}</span> : null}
          <span className="text-[color:var(--st-ink-dim)]">session: {sheet.sessionStatus}</span>
        </div>
      </header>

      {/*
        The customer is filling it in again, and this is the version they finished.

        The line staff need before they pull anything: what is below is the
        list the boutique is working from, and there is a newer one being
        written that is not finished and is therefore not this. Dated, because
        "they are redoing it" without saying which version is on the page is a
        sentence that makes a stylist trust neither.
      */}
      {sheet.refillInProgress ? (
        <div className="mb-5 st-notice">
          <p className="font-semibold text-[color:#6d5312]">The customer is filling this in again.</p>
          <p className="mt-1 text-sm text-[color:#6d5312]">
            Started {boutiqueDateTime(sheet.refillInProgress.startedAt)} and not sent yet. Below is the list they
            did send
            {sheet.sessionSubmittedAt ? `, on ${boutiqueDateTime(sheet.sessionSubmittedAt)}` : ''} — it stays the
            one to pull from until they finish the new one.
          </p>
        </div>
      ) : null}

      {/*
        Every version, so the earlier one can be opened and read. Kept rather
        than overwritten: what was first sent is often the thing a stylist
        wants to compare against, and it is half of what "fill it in again"
        promised the boutique.
      */}
      {sheet.versions.length > 1 ? (
        <div className="print-hide mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1 border bg-white p-3 text-sm" style={{ borderColor: 'var(--st-rule)' }}>
          <span className="text-[color:var(--st-ink-dim)]">Versions of this list:</span>
          {sheet.versions.map((version, index) => {
            const current = version.sessionId === sheet.sessionId
            return (
              <button
                key={version.sessionId}
                type="button"
                className={current ? 'font-semibold text-[color:var(--st-ink)] underline' : 'text-[color:var(--st-gold-deep)] underline'}
                aria-current={current ? 'true' : undefined}
                onClick={() => (version.authoritative ? setSearch({}) : setSearch({ session: version.sessionId }))}
              >
                {index + 1}. {boutiqueDateTime(version.submittedAt ?? version.createdAt)}
                {version.status === 'submitted' ? '' : ' (unsent)'}
                {version.authoritative ? ' — current' : ''}
              </button>
            )
          })}
        </div>
      ) : null}

      {readOnly ? (
        <p className="mb-5 st-card p-3 text-sm">
          You are reading an earlier version of this list. Outcomes are recorded on the current one.
        </p>
      ) : null}

      {/*
        Everything the customer typed, one row of the grid per screen that asked, so
        four measurements stay together and a wedding date does not end up
        sharing a line with a waist. Staff are the
        audience for all of it — the phone number to text them at, the date to
        plan the fitting against, the measurements to pull sizes for — and none
        of it has ever touched the ranking.
      */}
      {profileGroups.length > 0 || sheet.answers.length > 0 || sheet.lineLabel ? (
        <section className="mb-5 border bg-white p-3 print-slot" style={{ borderColor: 'var(--st-rule)' }}>
          <h2 className="mb-2 st-display text-lg">What the customer told us</h2>
          <div className="space-y-3">
            {/*
              What they chose rather than typed — how they are shopping, their
              size, the silhouettes they hope to try — in the back office's word
              for each, not the question's. The size is the one a stylist pulls
              samples by, so it sits beside the catalogue they browsed.
            */}
            {sheet.answers.length > 0 || sheet.lineLabel ? (
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
                {sheet.lineLabel ? (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-[color:var(--st-ink-dim)]">Shopping</dt>
                    <dd className="text-sm">{sheet.lineLabel}</dd>
                  </div>
                ) : null}
                {sheet.answers.map((answer) => (
                  <div key={answer.stepId}>
                    <dt className="text-xs uppercase tracking-wide text-[color:var(--st-ink-dim)]">{answer.label}</dt>
                    <dd className="text-sm">{answer.values.join(', ')}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {profileGroups.map((group) => (
              <dl key={group.stepId} className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
                {group.entries.map((entry) => (
                  <div key={entry.key} className={entry.value.length > 60 ? 'sm:col-span-2 lg:col-span-4' : undefined}>
                    <dt className="text-xs uppercase tracking-wide text-[color:var(--st-ink-dim)]">{entry.label}</dt>
                    <dd className="whitespace-pre-wrap text-sm">{entry.value}</dd>
                  </div>
                ))}
              </dl>
            ))}
          </div>
        </section>
      ) : null}

      {/*
        The customer who booked and never picked is the most common case
        staff have to absorb at 8am, so it gets a banner rather than an empty
        table — with whatever did get narrowed down as a starting point.
      */}
      {neverPicked ? (
        <div className="mb-5 st-notice">
          <p className="font-semibold text-[color:#6d5312]">Nothing was picked for this appointment.</p>
          <p className="mt-1 text-sm text-[color:#6d5312]">
            {Object.keys(sheet.lastFilters).length > 0
              ? 'The last filters set are below — start there rather than from the whole floor.'
              : 'No filters were set either. Start from the tier’s usual pulls.'}
          </p>
          {Object.keys(sheet.lastFilters).length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              {Object.entries(sheet.lastFilters).map(([key, values]) => (
                <li key={key}>
                  <span className="text-[color:#6d5312]">{humanize(key)}:</span> {values.map(humanize).join(', ')}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-6">
        {sheet.slots.map((slot) => (
          <section key={slot.key} className="print-slot">
            <div className="mb-1 flex items-baseline gap-3">
              <h2 className="st-display text-lg">{slot.label}</h2>
              <span className="text-sm text-[color:var(--st-ink-dim)] tabular-nums">
                {/* An unlimited list has no "of": its capacity is a stand-in, not a promise. */}
                {slot.limited === false ? slot.lines.length : `${slot.lines.length} of ${slot.capacity}`}
              </span>
            </div>
            {slot.lines.length === 0 ? (
              <p className="border bg-white p-3 text-sm text-[color:var(--st-ink-faint)]" style={{ borderColor: 'var(--st-rule)' }}>
                Nothing in this group.
              </p>
            ) : (
              <div className="border" style={{ borderColor: 'var(--st-rule)' }}>
                <LineTable
                  lines={slot.lines}
                  outcomes={outcomes}
                  onOutcome={(garmentId, value) => setOutcomes((current) => ({ ...current, [garmentId]: value }))}
                  readOnly={readOnly}
                />
              </div>
            )}
          </section>
        ))}

        {sheet.candidates.length > 0 ? (
          <section className="print-slot">
            <div className="mb-1 flex items-baseline gap-3">
              <h2 className="st-display text-lg">Candidates</h2>
              <span className="text-sm text-[color:var(--st-ink-dim)]">seen, or dropped in a later round</span>
            </div>
            <div className="border" style={{ borderColor: 'var(--st-rule)' }}>
              <LineTable
                lines={sheet.candidates}
                outcomes={outcomes}
                onOutcome={(garmentId, value) => setOutcomes((current) => ({ ...current, [garmentId]: value }))}
                readOnly={readOnly}
              />
            </div>
          </section>
        ) : null}
      </div>

      <section className="mt-6 border-t pt-4" style={{ borderColor: 'var(--st-rule-strong)' }}>
        <fieldset disabled={readOnly}>
          <legend className="mb-2 text-sm font-semibold">How did the appointment end?</legend>
          <div className="flex flex-wrap gap-4">
            {VISIT_OUTCOMES.map((value) => (
              <label key={value} className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name="visit-outcome"
                  value={value}
                  checked={visitOutcome === value}
                  onChange={() => setVisitOutcome(value)}
                />
                {humanize(value)}
              </label>
            ))}
          </div>
        </fieldset>
      </section>
    </div>
  )
}
